/**
 * Store Parameters Configuration Component
 * Includes: Door Classification (ABCD), Order Lead Time, and Store List for testing
 */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Card,
  CardContent,
  Divider,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayArrowIcon,
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { Parameter, ParameterType } from '../../types/parameters';

// Door classification thresholds (sqm)
const DEFAULT_DOOR_THRESHOLDS = {
  A: { min: 200, label: 'A-Door (Premium)', description: 'Large flagship & premium mall locations (>200 sqm)' },
  B: { min: 100, label: 'B-Door (Standard)', description: 'Mid-size high street & secondary mall (100-200 sqm)' },
  C: { min: 50, label: 'C-Door (Compact)', description: 'Smaller locations & tertiary malls (50-100 sqm)' },
  D: { min: 0, label: 'D-Door (Kiosk)', description: 'Small towns, kiosks & pop-ups (<50 sqm)' },
};

type DoorClass = 'A' | 'B' | 'C' | 'D';

interface DoorThresholds {
  A: { min: number; label: string; description: string };
  B: { min: number; label: string; description: string };
  C: { min: number; label: string; description: string };
  D: { min: number; label: string; description: string };
}

interface TestResult {
  storeId: string;
  storeName: string;
  status: 'success' | 'error' | 'running';
  message: string;
  doorClass?: DoorClass;
  leadTime?: number;
}

function classifyDoor(sizeSqm: number | null | undefined, thresholds: DoorThresholds): DoorClass {
  if (!sizeSqm) return 'D';
  if (sizeSqm >= thresholds.A.min) return 'A';
  if (sizeSqm >= thresholds.B.min) return 'B';
  if (sizeSqm >= thresholds.C.min) return 'C';
  return 'D';
}

function getDoorColor(doorClass: DoorClass): 'success' | 'primary' | 'warning' | 'default' {
  switch (doorClass) {
    case 'A': return 'success';
    case 'B': return 'primary';
    case 'C': return 'warning';
    case 'D': return 'default';
  }
}

const StoreParameters: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedStore, setSelectedStore] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingParam, setEditingParam] = useState<Parameter | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [subTab, setSubTab] = useState(0);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isTestRunning, setIsTestRunning] = useState(false);

  // Door classification thresholds (editable)
  const [doorThresholds, setDoorThresholds] = useState<DoorThresholds>(DEFAULT_DOOR_THRESHOLDS);

  // Fetch stores
  const { data: stores, isLoading: storesLoading } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const response = await api.getStores();
      return response.data;
    },
  });

  // Fetch store parameters
  const { data: parameters, isLoading } = useQuery<Parameter[]>({
    queryKey: ['parameters', 'store', selectedStore],
    queryFn: async () => {
      const response = await api.getParameters({
        param_type: ParameterType.STORE,
        entity_id: selectedStore,
      });
      return response.data;
    },
    enabled: !!selectedStore,
  });

  // Fetch door classification parameter (global)
  const { data: doorClassParam } = useQuery<Parameter[]>({
    queryKey: ['parameters', 'global', 'door_classification_thresholds'],
    queryFn: async () => {
      const response = await api.getParameters({
        param_type: ParameterType.GLOBAL,
        param_name: 'door_classification_thresholds',
      });
      return response.data;
    },
  });

  // Load saved thresholds if available
  React.useEffect(() => {
    if (doorClassParam && doorClassParam.length > 0) {
      const saved = doorClassParam[0].param_value;
      if (saved?.A && saved?.B && saved?.C && saved?.D) {
        setDoorThresholds(saved);
      }
    }
  }, [doorClassParam]);

  // Form state
  const [formData, setFormData] = useState({
    param_name: 'min_max_stock',
    min: 10,
    max: 100,
    coverage_days: 14,
    lead_time_days: 3,
    capacity_cubic_meters: 500,
    door_classification: 'A' as DoorClass,
    order_lead_time_warehouse: 2,
    order_lead_time_supplier: 7,
    order_lead_time_buffer: 1,
  });

  // Computed: stores with door classification
  const storesWithClassification = useMemo(() => {
    if (!stores) return [];
    return stores.map((store: any) => ({
      ...store,
      doorClass: classifyDoor(store.size_sqm, doorThresholds),
    }));
  }, [stores, doorThresholds]);

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      if (editingParam) {
        return await api.updateParameter(editingParam.param_id, data);
      } else {
        return await api.createParameter(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameters'] });
      setDialogOpen(false);
      setEditingParam(null);
      setSuccessMessage('Parameter saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  // Save door thresholds mutation
  const saveThresholdsMutation = useMutation({
    mutationFn: async (thresholds: DoorThresholds) => {
      // Check if the global parameter already exists
      if (doorClassParam && doorClassParam.length > 0) {
        return await api.updateParameter(doorClassParam[0].param_id, {
          param_value: thresholds,
          description: 'Door classification size thresholds (sqm) for ABCD classification',
        });
      } else {
        return await api.createParameter({
          param_type: ParameterType.GLOBAL,
          param_name: 'door_classification_thresholds',
          param_value: thresholds,
          description: 'Door classification size thresholds (sqm) for ABCD classification',
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameters'] });
      setSuccessMessage('Door classification thresholds saved!');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (paramId: string) => api.deleteParameter(paramId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameters'] });
      setSuccessMessage('Parameter deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  const handleSave = () => {
    const paramValue: Record<string, any> = {};

    switch (formData.param_name) {
      case 'min_max_stock':
        paramValue.min = formData.min;
        paramValue.max = formData.max;
        break;
      case 'coverage_days':
        paramValue.coverage_days = formData.coverage_days;
        break;
      case 'lead_time_days':
        paramValue.days = formData.lead_time_days;
        break;
      case 'storage_capacity':
        paramValue.capacity_cubic_meters = formData.capacity_cubic_meters;
        break;
      case 'door_classification':
        paramValue.door_class = formData.door_classification;
        paramValue.thresholds = doorThresholds;
        break;
      case 'order_lead_time':
        paramValue.warehouse_days = formData.order_lead_time_warehouse;
        paramValue.supplier_days = formData.order_lead_time_supplier;
        paramValue.buffer_days = formData.order_lead_time_buffer;
        paramValue.total_days = formData.order_lead_time_warehouse + formData.order_lead_time_supplier + formData.order_lead_time_buffer;
        break;
    }

    const paramData = {
      param_type: ParameterType.STORE,
      entity_id: selectedStore,
      param_name: formData.param_name,
      param_value: paramValue,
    };

    saveMutation.mutate(paramData);
  };

  const handleEdit = (param: Parameter) => {
    setEditingParam(param);

    if (param.param_value) {
      setFormData({
        param_name: param.param_name,
        min: param.param_value.min || 10,
        max: param.param_value.max || 100,
        coverage_days: param.param_value.coverage_days || 14,
        lead_time_days: param.param_value.days || 3,
        capacity_cubic_meters: param.param_value.capacity_cubic_meters || 500,
        door_classification: param.param_value.door_class || 'A',
        order_lead_time_warehouse: param.param_value.warehouse_days || 2,
        order_lead_time_supplier: param.param_value.supplier_days || 7,
        order_lead_time_buffer: param.param_value.buffer_days || 1,
      });
    }

    setDialogOpen(true);
  };

  const handleDelete = (paramId: string) => {
    if (window.confirm('Are you sure you want to delete this parameter?')) {
      deleteMutation.mutate(paramId);
    }
  };

  // Test runner: validates parameters against stores
  const handleRunTests = async (storeIds?: string[]) => {
    setIsTestRunning(true);
    const targetStores = storeIds
      ? storesWithClassification.filter((s: any) => storeIds.includes(s.store_id))
      : storesWithClassification;

    const results: TestResult[] = [];

    for (const store of targetStores) {
      results.push({
        storeId: store.store_id,
        storeName: store.store_name,
        status: 'running',
        message: 'Testing...',
      });
    }
    setTestResults([...results]);

    // Simulate test execution against each store
    for (let i = 0; i < targetStores.length; i++) {
      const store = targetStores[i];
      try {
        // Fetch the store's parameters to validate
        const response = await api.getParameters({
          param_type: ParameterType.STORE,
          entity_id: store.store_id,
        });
        const storeParams = response.data as Parameter[];
        const doorClass = classifyDoor(store.size_sqm, doorThresholds);

        // Find order lead time parameter
        const leadTimeParam = storeParams.find(
          (p: Parameter) => p.param_name === 'order_lead_time' || p.param_name === 'lead_time_days'
        );
        const leadTimeDays = leadTimeParam?.param_value?.total_days || leadTimeParam?.param_value?.days || null;

        const warnings: string[] = [];
        if (!store.size_sqm) warnings.push('Missing store size (sqm)');
        if (!leadTimeParam) warnings.push('No lead time configured');
        if (storeParams.length === 0) warnings.push('No parameters configured');

        results[i] = {
          storeId: store.store_id,
          storeName: store.store_name,
          status: warnings.length > 0 ? 'error' : 'success',
          message: warnings.length > 0 ? warnings.join('; ') : `OK - ${storeParams.length} params configured`,
          doorClass: doorClass,
          leadTime: leadTimeDays,
        };
      } catch {
        results[i] = {
          storeId: store.store_id,
          storeName: store.store_name,
          status: 'error',
          message: 'Failed to fetch store parameters',
        };
      }
      setTestResults([...results]);
    }

    setIsTestRunning(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Sub-tabs for store management sections */}
      <Box sx={{ borderBottom: '1px solid var(--color-border, #e0e0e0)', mb: 3 }}>
        <Tabs value={subTab} onChange={(_e, v) => setSubTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Store Parameters" />
          <Tab label="Door Classification (ABCD)" />
          <Tab label="Order Lead Time" />
          <Tab label="Store List & Tests" />
        </Tabs>
      </Box>

      {/* ================================================================== */}
      {/* TAB 0: Store Parameters (original) */}
      {/* ================================================================== */}
      {subTab === 0 && (
        <Box>
          {/* Store Selector */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Store</InputLabel>
                <Select
                  value={selectedStore}
                  label="Select Store"
                  onChange={(e) => setSelectedStore(e.target.value)}
                >
                  {stores?.map((store: any) => (
                    <MenuItem key={store.store_id} value={store.store_id}>
                      {store.store_name} ({store.store_type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {selectedStore && (
              <Grid item xs={12} md={6}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                  Add Parameter
                </Button>
              </Grid>
            )}
          </Grid>

          {/* Parameters Table */}
          {selectedStore && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Store Parameters
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Parameter Name</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : parameters && parameters.length > 0 ? (
                      parameters.map((param) => (
                        <TableRow key={param.param_id}>
                          <TableCell>
                            <Chip label={param.param_name} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" component="pre">
                              {JSON.stringify(param.param_value, null, 2)}
                            </Typography>
                          </TableCell>
                          <TableCell>{param.description || '-'}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleEdit(param)} color="primary">
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(param.param_id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No parameters configured for this store. Click "Add Parameter" to get started.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}

      {/* ================================================================== */}
      {/* TAB 1: Door Classification (ABCD) */}
      {/* ================================================================== */}
      {subTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Door Classification (ABCD)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Define size thresholds (in sqm) to automatically classify stores into A, B, C, or D doors.
            Classification is based on total store floor area.
          </Typography>

          {/* Threshold Configuration */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Size Thresholds Configuration
              </Typography>
              <Grid container spacing={3}>
                {(['A', 'B', 'C', 'D'] as DoorClass[]).map((door) => (
                  <Grid item xs={12} sm={6} md={3} key={door}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderColor: door === 'A' ? 'success.main' : door === 'B' ? 'primary.main' : door === 'C' ? 'warning.main' : 'grey.400',
                        borderWidth: 2,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {door}-Door
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {doorThresholds[door].description}
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        label={door === 'D' ? 'Below (sqm)' : 'Min Size (sqm)'}
                        value={doorThresholds[door].min}
                        onChange={(e) =>
                          setDoorThresholds({
                            ...doorThresholds,
                            [door]: { ...doorThresholds[door], min: parseInt(e.target.value) || 0 },
                          })
                        }
                        disabled={door === 'D'}
                        size="small"
                        helperText={door === 'D' ? 'Auto: below C threshold' : `>= ${doorThresholds[door].min} sqm`}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => saveThresholdsMutation.mutate(doorThresholds)}
                  disabled={saveThresholdsMutation.isPending}
                >
                  Save Thresholds
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setDoorThresholds(DEFAULT_DOOR_THRESHOLDS)}
                >
                  Reset to Defaults
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Classification Summary */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Current Store Classification
              </Typography>
              {storesLoading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  {/* Summary chips */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    {(['A', 'B', 'C', 'D'] as DoorClass[]).map((door) => {
                      const count = storesWithClassification.filter((s: any) => s.doorClass === door).length;
                      return (
                        <Chip
                          key={door}
                          label={`${door}-Door: ${count} stores`}
                          color={getDoorColor(door)}
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Store</TableCell>
                          <TableCell>Code</TableCell>
                          <TableCell>City</TableCell>
                          <TableCell align="center">Size (sqm)</TableCell>
                          <TableCell align="center">Door Class</TableCell>
                          <TableCell>Store Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {storesWithClassification.map((store: any) => (
                          <TableRow key={store.store_id} hover>
                            <TableCell>{store.store_name}</TableCell>
                            <TableCell>{store.store_code}</TableCell>
                            <TableCell>{store.city || '-'}</TableCell>
                            <TableCell align="center">{store.size_sqm ?? 'N/A'}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${store.doorClass}-Door`}
                                color={getDoorColor(store.doorClass)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{store.store_type || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* ================================================================== */}
      {/* TAB 2: Order Lead Time */}
      {/* ================================================================== */}
      {subTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Order Lead Time Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure the total order lead time per store. Lead time is composed of warehouse processing,
            supplier delivery, and a safety buffer. This parameter drives replenishment calculations,
            safety stock levels, and reorder point computations.
          </Typography>

          {/* Store Selector */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Store</InputLabel>
                <Select
                  value={selectedStore}
                  label="Select Store"
                  onChange={(e) => setSelectedStore(e.target.value)}
                >
                  {stores?.map((store: any) => (
                    <MenuItem key={store.store_id} value={store.store_id}>
                      {store.store_name} ({store.store_type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {selectedStore && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Lead Time Components
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Warehouse Processing (days)"
                      value={formData.order_lead_time_warehouse}
                      onChange={(e) =>
                        setFormData({ ...formData, order_lead_time_warehouse: parseInt(e.target.value) || 0 })
                      }
                      helperText="Time for warehouse to pick, pack, and dispatch the order"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Supplier Delivery (days)"
                      value={formData.order_lead_time_supplier}
                      onChange={(e) =>
                        setFormData({ ...formData, order_lead_time_supplier: parseInt(e.target.value) || 0 })
                      }
                      helperText="Transit time from supplier/DC to store"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Safety Buffer (days)"
                      value={formData.order_lead_time_buffer}
                      onChange={(e) =>
                        setFormData({ ...formData, order_lead_time_buffer: parseInt(e.target.value) || 0 })
                      }
                      helperText="Additional buffer for delays and uncertainties"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Total lead time summary */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Total Order Lead Time:
                  </Typography>
                  <Chip
                    label={`${formData.order_lead_time_warehouse + formData.order_lead_time_supplier + formData.order_lead_time_buffer} days`}
                    color="primary"
                    sx={{ fontSize: '1rem', fontWeight: 700, py: 2, px: 1 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  = Warehouse ({formData.order_lead_time_warehouse}d) + Supplier ({formData.order_lead_time_supplier}d) + Buffer ({formData.order_lead_time_buffer}d)
                </Typography>

                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => {
                      const paramData = {
                        param_type: ParameterType.STORE,
                        entity_id: selectedStore,
                        param_name: 'order_lead_time',
                        param_value: {
                          warehouse_days: formData.order_lead_time_warehouse,
                          supplier_days: formData.order_lead_time_supplier,
                          buffer_days: formData.order_lead_time_buffer,
                          total_days: formData.order_lead_time_warehouse + formData.order_lead_time_supplier + formData.order_lead_time_buffer,
                        },
                      };
                      saveMutation.mutate(paramData);
                    }}
                    disabled={saveMutation.isPending}
                  >
                    Save Lead Time
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {!selectedStore && (
            <Alert severity="info" icon={<InfoIcon />}>
              Select a store above to configure its order lead time parameters.
            </Alert>
          )}
        </Box>
      )}

      {/* ================================================================== */}
      {/* TAB 3: Store List & Tests */}
      {/* ================================================================== */}
      {subTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Store List
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Complete list of active stores with their door classification and parameter status.
            Use the test runner to validate that all stores have the required parameters configured.
          </Typography>

          {/* Test controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isTestRunning ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
              onClick={() => handleRunTests()}
              disabled={isTestRunning || !stores || stores.length === 0}
            >
              {isTestRunning ? 'Running Tests...' : 'Run Tests on All Stores'}
            </Button>
            {testResults.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${testResults.filter((r) => r.status === 'success').length} passed`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<ErrorIcon />}
                  label={`${testResults.filter((r) => r.status === 'error').length} issues`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>

          {/* Store List Table */}
          {storesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Store Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell align="center">Size (sqm)</TableCell>
                    <TableCell align="center">Door Class</TableCell>
                    <TableCell align="center">Test Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storesWithClassification.length > 0 ? (
                    storesWithClassification.map((store: any) => {
                      const testResult = testResults.find((r) => r.storeId === store.store_id);
                      return (
                        <TableRow key={store.store_id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <StoreIcon fontSize="small" color="action" />
                              {store.store_name}
                            </Box>
                          </TableCell>
                          <TableCell>{store.store_code}</TableCell>
                          <TableCell>{store.store_type || '-'}</TableCell>
                          <TableCell>{store.city || '-'}</TableCell>
                          <TableCell align="center">{store.size_sqm ?? 'N/A'}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${store.doorClass}-Door`}
                              color={getDoorColor(store.doorClass)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {testResult ? (
                              <Tooltip title={testResult.message}>
                                <Chip
                                  icon={
                                    testResult.status === 'success' ? (
                                      <CheckCircleIcon />
                                    ) : testResult.status === 'running' ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <ErrorIcon />
                                    )
                                  }
                                  label={
                                    testResult.status === 'success'
                                      ? 'Pass'
                                      : testResult.status === 'running'
                                        ? 'Running'
                                        : 'Issues'
                                  }
                                  color={
                                    testResult.status === 'success'
                                      ? 'success'
                                      : testResult.status === 'error'
                                        ? 'error'
                                        : 'default'
                                  }
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Run test for this store">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleRunTests([store.store_id])}
                                disabled={isTestRunning}
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View/edit parameters">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setSelectedStore(store.store_id);
                                  setSubTab(0);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No stores found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Test Results Detail (accordion) */}
          {testResults.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Test Results Detail
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Store</TableCell>
                          <TableCell>Test Status</TableCell>
                          <TableCell>Door Class</TableCell>
                          <TableCell>Lead Time</TableCell>
                          <TableCell>Message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {testResults.map((result) => (
                          <TableRow key={result.storeId}>
                            <TableCell>{result.storeName}</TableCell>
                            <TableCell>
                              {result.status === 'success' ? (
                                <CheckCircleIcon color="success" fontSize="small" />
                              ) : result.status === 'running' ? (
                                <CircularProgress size={16} />
                              ) : (
                                <ErrorIcon color="error" fontSize="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              {result.doorClass ? (
                                <Chip label={`${result.doorClass}-Door`} color={getDoorColor(result.doorClass)} size="small" />
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {result.leadTime != null ? `${result.leadTime} days` : 'Not set'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{result.message}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingParam ? 'Edit Parameter' : 'Add Parameter'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Parameter Type</InputLabel>
                <Select
                  value={formData.param_name}
                  label="Parameter Type"
                  onChange={(e) => setFormData({ ...formData, param_name: e.target.value })}
                  disabled={!!editingParam}
                >
                  <MenuItem value="min_max_stock">Min/Max Stock Levels</MenuItem>
                  <MenuItem value="coverage_days">Coverage Days</MenuItem>
                  <MenuItem value="lead_time_days">Lead Time Days</MenuItem>
                  <MenuItem value="storage_capacity">Storage Capacity</MenuItem>
                  <MenuItem value="door_classification">Door Classification</MenuItem>
                  <MenuItem value="order_lead_time">Order Lead Time (Detailed)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.param_name === 'min_max_stock' && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Minimum Stock"
                    value={formData.min}
                    onChange={(e) => setFormData({ ...formData, min: parseInt(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Maximum Stock"
                    value={formData.max}
                    onChange={(e) => setFormData({ ...formData, max: parseInt(e.target.value) })}
                  />
                </Grid>
              </>
            )}

            {formData.param_name === 'coverage_days' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Coverage Days"
                  value={formData.coverage_days}
                  onChange={(e) =>
                    setFormData({ ...formData, coverage_days: parseInt(e.target.value) })
                  }
                  helperText="Target days of inventory to maintain"
                />
              </Grid>
            )}

            {formData.param_name === 'lead_time_days' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Lead Time Days"
                  value={formData.lead_time_days}
                  onChange={(e) =>
                    setFormData({ ...formData, lead_time_days: parseInt(e.target.value) })
                  }
                  helperText="Days from order to delivery"
                />
              </Grid>
            )}

            {formData.param_name === 'storage_capacity' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Storage Capacity (Cubic Meters)"
                  value={formData.capacity_cubic_meters}
                  onChange={(e) =>
                    setFormData({ ...formData, capacity_cubic_meters: parseFloat(e.target.value) })
                  }
                  helperText="Total storage capacity of the store"
                />
              </Grid>
            )}

            {formData.param_name === 'door_classification' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Door Classification</InputLabel>
                  <Select
                    value={formData.door_classification}
                    label="Door Classification"
                    onChange={(e) =>
                      setFormData({ ...formData, door_classification: e.target.value as DoorClass })
                    }
                  >
                    <MenuItem value="A">A-Door (Premium, &gt;{doorThresholds.A.min} sqm)</MenuItem>
                    <MenuItem value="B">B-Door (Standard, {doorThresholds.B.min}-{doorThresholds.A.min} sqm)</MenuItem>
                    <MenuItem value="C">C-Door (Compact, {doorThresholds.C.min}-{doorThresholds.B.min} sqm)</MenuItem>
                    <MenuItem value="D">D-Door (Kiosk, &lt;{doorThresholds.C.min} sqm)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Override the automatic door classification for this store. The automatic classification is based on store size (sqm).
                </Typography>
              </Grid>
            )}

            {formData.param_name === 'order_lead_time' && (
              <>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Warehouse (days)"
                    value={formData.order_lead_time_warehouse}
                    onChange={(e) =>
                      setFormData({ ...formData, order_lead_time_warehouse: parseInt(e.target.value) || 0 })
                    }
                    helperText="Pick, pack, dispatch"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Supplier (days)"
                    value={formData.order_lead_time_supplier}
                    onChange={(e) =>
                      setFormData({ ...formData, order_lead_time_supplier: parseInt(e.target.value) || 0 })
                    }
                    helperText="Transit time"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Buffer (days)"
                    value={formData.order_lead_time_buffer}
                    onChange={(e) =>
                      setFormData({ ...formData, order_lead_time_buffer: parseInt(e.target.value) || 0 })
                    }
                    helperText="Safety margin"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Total lead time: <strong>
                      {formData.order_lead_time_warehouse + formData.order_lead_time_supplier + formData.order_lead_time_buffer} days
                    </strong>
                  </Alert>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saveMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreParameters;
